import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import MainLayout from '../components/layout/MainLayout';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { RightPanel } from '../components/ui/right-panel';
import {
  Tooltip as UITooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {
  Users, ClipboardList, Cpu, ChevronRight, Loader2,
  CheckCircle, AlertTriangle, Play, RotateCcw, Target,
  Shield, ArrowRight, Clock, Eye, Circle
} from 'lucide-react';
import { Coachmark } from '../components/Coachmark';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, Cell
} from 'recharts';

const MODULES = [
  { id: 'people', title: 'Mensen', icon: Users, color: 'blue', description: 'Medewerkers & bewustzijn', time: '~5 min', href: '/people', btnLabel: 'Start vragen' },
  { id: 'process', title: 'Organisatie', icon: ClipboardList, color: 'violet', description: 'Processen & beleid', time: '~15-20 min', href: '/process', btnLabel: 'Start vragen' },
  { id: 'technology', title: 'Systemen', icon: Cpu, color: 'emerald', description: 'Systemen & scans', time: '~10 min', href: '/technology', btnLabel: 'Start scan' },
];

import { SectionTabs, SECTIONS } from '../components/enterprise';

const AssessmentPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [moduleStats, setModuleStats] = useState({
    people: { completed: 0, total: 0, score: 0 },
    process: { completed: 0, total: 0, score: 0 },
    technology: { completed: 0, total: 0, score: 0 }
  });
  const [activeReassessment, setActiveReassessment] = useState(null);
  const [completedReassessments, setCompletedReassessments] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [selectedHistory, setSelectedHistory] = useState(null);
  const [hrAnswers, setHrAnswers] = useState(null);
  const [answersLoading, setAnswersLoading] = useState(false);
  const [nulmetingSnapshot, setNulmetingSnapshot] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [peopleRes, processRes, techRes, reassessRes, spCtx] = await Promise.all([
        api.get('/people/stats').catch(() => ({ data: { completed: 0, total: 10, score: 0 } })),
        api.get('/process/stats').catch(() => ({ data: { completed: 0, total: 10, score: 0 } })),
        api.get('/technology/stats').catch(() => ({ data: { completed: 0, total: 10, score: 0 } })),
        api.get('/assessments/reassessments').catch(() => ({ data: [] })),
        api.get('/assessments/active-reassessment-context').catch(() => ({ data: { active: false } }))
      ]);
      setModuleStats({
        people: peopleRes.data || { completed: 0, total: 10, score: 0 },
        process: processRes.data || { completed: 0, total: 10, score: 0 },
        technology: techRes.data || { completed: 0, total: 10, score: 0 }
      });
      const reassessments = reassessRes.data || [];
      setActiveReassessment(reassessments.find(r => r.status === 'in_progress') || null);
      setCompletedReassessments(reassessments.filter(r => r.status === 'completed'));
      setNulmetingSnapshot(spCtx.data?.nulmeting_snapshot || null);
    } catch {
      setFetchError(true);
    } finally { setLoading(false); }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const isModuleReassessing = (id) => {
    if (!activeReassessment) return false;
    return (activeReassessment.modules || []).includes(id) || (activeReassessment.modules || []).includes('all');
  };

  const getModuleSteps = (id) => {
    const s = moduleStats[id];
    const reassessing = isModuleReassessing(id);
    if (id === 'people') {
      return [
        { label: 'Risk assessment invullen', done: !reassessing && s?.hr_assessment_status === 'completed' },
        { label: 'Campagne aanmaken', done: !reassessing && (s?.total_campaigns || 0) > 0 },
        { label: 'Campagne versturen', done: !reassessing && (s?.total_sent || 0) > 0 },
      ];
    }
    if (id === 'process') {
      const completed = reassessing ? 0 : (s?.completed || 0);
      const inProgress = reassessing ? 0 : (s?.in_progress || 0);
      return [
        { label: 'Framework kiezen', done: completed > 0 || inProgress > 0 },
        { label: 'Vragenlijst invullen', done: completed > 0 },
        { label: 'Tweede framework', done: completed >= 2 },
      ];
    }
    if (id === 'technology') {
      const hasTargets = !reassessing && (s?.total_targets || 0) > 0;
      const hasScans = !reassessing && (s?.completed_scans || 0) > 0;
      return [
        { label: 'Target toevoegen', done: hasTargets },
        { label: 'Scan uitvoeren', done: hasScans },
        { label: 'Resultaten beoordelen', done: hasScans && (s?.total_vulnerabilities || 0) > 0 },
      ];
    }
    return [];
  };

  const getProgress = (id) => {
    const steps = getModuleSteps(id);
    if (!steps.length) return 0;
    return Math.round((steps.filter(s => s.done).length / steps.length) * 100);
  };

  const getStatus = (id) => {
    if (isModuleReassessing(id)) return 'reassessing';
    const p = getProgress(id);
    return p === 0 ? 'not_started' : p === 100 ? 'completed' : 'in_progress';
  };

  const getModuleActualScore = (id) => {
    const s = moduleStats[id];
    if (id === 'people') {
      if (s?.total_sent > 0) return Math.round((1 - (s.total_clicked || 0) / s.total_sent) * 100);
      return s?.hr_assessment_status === 'completed' ? 100 : 0;
    }
    if (id === 'process') return s?.avg_score || 0;
    if (id === 'technology') {
      if ((s?.completed_scans || 0) > 0) return 100;
      if ((s?.total_targets || 0) > 0) return 50;
      return 0;
    }
    return 0;
  };

  const getModuleScoreLabel = (id) => {
    const s = moduleStats[id];
    if (id === 'people') return `${s?.total_sent > 0 ? Math.round((1 - (s.total_clicked || 0) / s.total_sent) * 100) : 0}% bewustzijn`;
    if (id === 'process') return `${s?.avg_score || 0}% compliance`;
    if (id === 'technology') return `${s?.total_vulnerabilities || 0} kwetsbaarheden`;
    return '';
  };

  const getModuleDetailMetrics = (id) => {
    const s = moduleStats[id];
    if (id === 'people') return [
      { label: 'Campagnes', value: s?.total_campaigns || 0 },
      { label: 'E-mails verstuurd', value: s?.total_sent || 0 },
      { label: 'HR assessment', value: s?.hr_assessment_status === 'completed' ? 'Afgerond' : s?.hr_assessment_status === 'in_progress' ? 'Bezig' : 'Niet gestart' },
    ];
    if (id === 'process') return [
      { label: 'Voltooid', value: s?.completed || 0 },
      { label: 'Bezig', value: s?.in_progress || 0 },
      { label: 'Gem. score', value: `${s?.avg_score || 0}%` },
    ];
    if (id === 'technology') return [
      { label: 'Targets', value: s?.total_targets || 0 },
      { label: 'Scans', value: s?.completed_scans || 0 },
      { label: 'Kwetsbaarheden', value: s?.total_vulnerabilities || 0 },
    ];
    return [];
  };

  const totalProgress = activeReassessment ? 0 : Math.round((getProgress('people') + getProgress('process') + getProgress('technology')) / 3);
  const nulmetingComplete = !activeReassessment && MODULES.every(m => getStatus(m.id) === 'completed');
  const nulmetingStarted = MODULES.some(m => getStatus(m.id) !== 'not_started' && getStatus(m.id) !== 'reassessing');
  const hasCompletedNulmeting = nulmetingComplete || !!activeReassessment || completedReassessments.length > 0;
  const isSteekproef = !!activeReassessment;
  const totalScore = Math.round(
    (getModuleActualScore('people') + getModuleActualScore('process') + getModuleActualScore('technology')) / 3
  );

  const getNextStep = () => {
    for (const mod of MODULES) {
      if (getStatus(mod.id) !== 'completed') return mod;
    }
    return null;
  };

  const fetchAnswers = async () => {
    setAnswersLoading(true);
    try {
      const res = await api.get('/people/assessment/answers');
      setHrAnswers(res.data);
    } catch { setHrAnswers(null); }
    finally { setAnswersLoading(false); }
  };

  // Chart data for history summary
  const chartData = MODULES.map(mod => ({
    name: mod.title,
    voortgang: getProgress(mod.id),
    fill: getStatus(mod.id) === 'completed' ? '#16a34a' : getProgress(mod.id) > 0 ? '#2563eb' : '#e5e5e5'
  }));

  // Determine which panel is open
  const panelOpen = selectedDomain || selectedHistory;
  const selectedMod = selectedDomain ? MODULES.find(m => m.id === selectedDomain) : null;

  if (loading) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-white">
          <div className="mx-auto max-w-5xl px-6 py-10 space-y-10">
            <Skeleton className="h-8 w-48" /><Skeleton className="h-4 w-80" />
            <div className="flex items-center gap-3 mt-4"><Loader2 className="h-4 w-4 animate-spin text-neutral-400" strokeWidth={1.5} /><p className="text-xs text-neutral-500">Laden...</p></div>
            <div className="grid grid-cols-3 gap-6"><Skeleton className="h-28" /><Skeleton className="h-28" /><Skeleton className="h-28" /></div>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (fetchError) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-white">
          <div className="mx-auto max-w-5xl px-6 py-10">
            <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="assessment-error-state">
              <AlertTriangle className="h-10 w-10 text-red-400 mb-4" strokeWidth={1.5} />
              <h2 className="text-base font-semibold text-black mb-1">Laden mislukt</h2>
              <p className="text-sm text-neutral-500 mb-4">Controleer je verbinding en probeer opnieuw.</p>
              <Button onClick={fetchData} className="bg-black text-white hover:bg-neutral-800">Opnieuw</Button>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="min-h-screen bg-white" data-testid="assessment-page">
        <SectionTabs tabs={SECTIONS.beoordelen.tabs} />
        <div className="mx-auto max-w-5xl px-6 py-10">

          {/* HEADER */}
          <header className="mb-12" data-testid="assessment-header">
            <p className="spk-section-label">Analyse</p>
            <h1 className="text-2xl font-semibold tracking-tight text-black mt-1">
              {nulmetingComplete && !isSteekproef ? 'Analyse compleet' : isSteekproef ? 'Steekproef' : 'Nulmeting'}
            </h1>
            <p className="mt-2 text-sm text-neutral-500 max-w-xl">
              {nulmetingComplete && !isSteekproef
                ? 'Alle domeinen zijn afgerond. Bekijk resultaten of plan een steekproef.'
                : 'Doorloop drie domeinen voor een compleet beeld van je beveiliging.'}
            </p>
          </header>

          <main className="space-y-12">

            {/* Coachmark */}
            {!nulmetingComplete && !nulmetingStarted && (
              <Coachmark
                id="assessment-intro"
                text="De nulmeting bestaat uit 3 domeinen: Mensen, Organisatie en Systemen. Je voortgang wordt automatisch bewaard."
              />
            )}

            {/* PROGRESS BAR (als gestart) */}
            {(nulmetingStarted || isSteekproef) && (
              <div className="flex items-center gap-4 p-5 rounded-xl border border-neutral-200" data-testid="progress-card">
                <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${nulmetingComplete ? 'bg-green-100' : isSteekproef ? 'bg-amber-100' : 'bg-neutral-100'}`}>
                  {nulmetingComplete ? <CheckCircle className="h-5 w-5 text-green-600" strokeWidth={1.5} /> : isSteekproef ? <RotateCcw className="h-5 w-5 text-amber-600" strokeWidth={1.5} /> : <Target className="h-5 w-5 text-neutral-500" strokeWidth={1.5} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-semibold text-black">{isSteekproef ? 'Steekproef' : 'Nulmeting'}</span>
                    {isSteekproef && <Badge className="bg-amber-100 text-amber-700 text-[10px]">Actief</Badge>}
                  </div>
                  {!nulmetingComplete && <Progress value={totalProgress} className="h-1.5 mt-1" />}
                </div>
                <span className="text-xl font-bold text-black shrink-0">{nulmetingComplete ? `${totalScore}%` : `${totalProgress}%`}</span>
              </div>
            )}

            {/* DOMAIN CARDS — compact, klikbaar */}
            <section data-testid="domain-overview">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {MODULES.map((mod) => {
                  const Icon = mod.icon;
                  const progress = getProgress(mod.id);
                  const status = getStatus(mod.id);
                  const completed = status === 'completed';
                  const reassessing = status === 'reassessing';
                  const isActive = selectedDomain === mod.id;

                  return (
                    <Card
                      key={mod.id}
                      className={`border cursor-pointer transition-all ${
                        isActive ? 'border-black shadow-md ring-1 ring-black' :
                        completed ? 'border-green-200 hover:border-green-300' :
                        reassessing ? 'border-amber-200 hover:border-amber-300' :
                        'border-neutral-200 hover:border-neutral-300'
                      } hover:shadow-sm`}
                      onClick={() => { setSelectedHistory(null); setSelectedDomain(mod.id); }}
                      data-testid={`domain-card-${mod.id}`}
                    >
                      <CardContent className="p-5">
                        <div className="flex items-start justify-between mb-4">
                          <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${
                            completed ? 'bg-green-100' : reassessing ? 'bg-amber-100' : 'bg-neutral-100'
                          }`}>
                            {completed ? <CheckCircle className="h-5 w-5 text-green-600" strokeWidth={1.5} />
                              : reassessing ? <RotateCcw className="h-5 w-5 text-amber-600" strokeWidth={1.5} />
                              : <Icon className="h-5 w-5 text-neutral-500" strokeWidth={1.5} />}
                          </div>
                          <span className={`text-lg font-bold ${completed ? 'text-green-600' : progress > 0 ? 'text-black' : 'text-neutral-300'}`}>
                            {progress}%
                          </span>
                        </div>
                        <h3 className="text-sm font-semibold text-black">{mod.title}</h3>
                        <p className="text-xs text-neutral-500 mt-0.5">{mod.description}</p>
                        {!completed && progress > 0 && (
                          <Progress value={progress} className="h-1 mt-3" />
                        )}
                        {completed && (
                          <p className="text-[11px] text-green-600 font-medium mt-3">Voltooid</p>
                        )}
                        {!nulmetingStarted && !isSteekproef && progress === 0 && (
                          <div className="flex items-center gap-1 mt-3 text-[11px] text-neutral-400">
                            <Clock className="h-3 w-3" strokeWidth={1.5} />
                            <span>{mod.time}</span>
                          </div>
                        )}
                        {/* Nulmeting referentie bij actieve steekproef */}
                        {reassessing && nulmetingSnapshot && (() => {
                          const scoreKey = `${mod.id === 'people' ? 'people' : mod.id === 'process' ? 'process' : 'technology'}_score`;
                          const nulScore = nulmetingSnapshot[scoreKey];
                          return nulScore !== null && nulScore !== undefined ? (
                            <div className="mt-3 pt-2 border-t border-amber-200/60 flex items-center justify-between" data-testid={`nulmeting-ref-${mod.id}`}>
                              <span className="text-[10px] text-neutral-400">Nulmeting</span>
                              <span className="text-[11px] font-medium text-neutral-500">{nulScore}%</span>
                            </div>
                          ) : null;
                        })()}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </section>

            {/* NEXT STEP CTA */}
            {!nulmetingComplete && !isSteekproef && (() => {
              const next = getNextStep();
              return next ? (
                <div
                  className="rounded-xl border-2 border-black p-5 flex items-center justify-between cursor-pointer hover:bg-neutral-50 transition-colors"
                  onClick={() => navigate(next.href)}
                  data-testid="assessment-next-cta-block"
                >
                  <div>
                    <p className="text-sm font-semibold text-black">
                      {getStatus(next.id) === 'in_progress' ? `Ga verder met ${next.title}` : `Begin met ${next.title}`}
                    </p>
                    <p className="text-xs text-neutral-500 mt-0.5">{next.description}</p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-black shrink-0" strokeWidth={1.5} />
                </div>
              ) : null;
            })()}

            {/* COMPLETED: score overview */}
            {nulmetingComplete && !isSteekproef && (
              <section className="space-y-6" data-testid="analysis-complete">
                <div className="grid grid-cols-3 gap-4">
                  {MODULES.map(mod => (
                    <div key={mod.id} className="text-center p-5 rounded-xl border border-neutral-200">
                      <mod.icon className="h-5 w-5 mx-auto text-neutral-400 mb-2" strokeWidth={1.5} />
                      <p className="text-2xl font-bold text-black">{getModuleActualScore(mod.id)}%</p>
                      <p className="text-xs text-neutral-500 mt-0.5">{mod.title}</p>
                      <p className="text-[10px] text-neutral-400 mt-0.5">{getModuleScoreLabel(mod.id)}</p>
                    </div>
                  ))}
                </div>
                <div
                  className="rounded-xl border-2 border-black p-5 flex items-center justify-between cursor-pointer hover:bg-neutral-50 transition-colors"
                  onClick={() => navigate('/risks')}
                  data-testid="go-to-risks-cta"
                >
                  <div>
                    <p className="text-sm font-semibold text-black">Bekijk vastgestelde risico's</p>
                    <p className="text-xs text-neutral-500 mt-0.5">Op basis van jouw analyse zijn risico's en kwetsbaarheden vastgesteld.</p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-black shrink-0" strokeWidth={1.5} />
                </div>
              </section>
            )}

            {/* STEEKPROEF BANNER — compact */}
            {isSteekproef && (
              <div className="flex items-center justify-between p-3 rounded-lg border border-neutral-200 bg-neutral-50/50" data-testid="steekproef-info">
                <div className="flex items-center gap-2">
                  <RotateCcw className="h-3.5 w-3.5 text-amber-500" strokeWidth={1.5} />
                  <span className="text-xs text-neutral-600">
                    Steekproef actief{activeReassessment?.started_at && `, ${new Date(activeReassessment.started_at).toLocaleDateString('nl-NL')}`}
                  </span>
                </div>
                <Button variant="ghost" size="sm" className="text-xs text-neutral-500 h-7" onClick={() => navigate('/reassessment')} data-testid="go-to-reassessment">
                  Details <ChevronRight className="h-3 w-3 ml-0.5" strokeWidth={1.5} />
                </Button>
              </div>
            )}

            {/* HISTORY */}
            {hasCompletedNulmeting && (
              <section data-testid="assessment-history">
                <div className="flex items-center justify-between mb-5">
                  <span className="spk-section-label mb-0">Uitgevoerde metingen</span>
                  {nulmetingComplete && (
                    <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => navigate('/reassessment')} data-testid="start-reassessment-btn">
                      <RotateCcw className="h-3 w-3 mr-1" strokeWidth={1.5} /> Nieuwe steekproef
                    </Button>
                  )}
                </div>

                {/* Mini bar chart */}
                <div className="h-20 mb-5" data-testid="history-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }} barSize={28}>
                      <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#a3a3a3' }} axisLine={false} tickLine={false} />
                      <Bar dataKey="voortgang" radius={[4, 4, 0, 0]}>
                        {chartData.map((entry, index) => (
                          <Cell key={index} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Compact history table */}
                <div className="rounded-xl border border-neutral-200 overflow-hidden" data-testid="assessment-history-table-wrapper">
                  <table className="w-full" data-testid="assessment-history-table">
                    <thead>
                      <tr className="border-b border-neutral-200">
                        <th className="p-3 text-left text-[11px] font-bold uppercase tracking-wider text-neutral-400 bg-neutral-50">Meting</th>
                        <th className="p-3 text-left text-[11px] font-bold uppercase tracking-wider text-neutral-400 bg-neutral-50">Datum</th>
                        <th className="p-3 text-left text-[11px] font-bold uppercase tracking-wider text-neutral-400 bg-neutral-50">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        className={`border-b border-neutral-100 cursor-pointer transition-colors ${selectedHistory === 'nulmeting' ? 'bg-neutral-100' : 'hover:bg-neutral-50'}`}
                        onClick={() => { setSelectedDomain(null); setSelectedHistory('nulmeting'); }}
                        data-testid="history-row-nulmeting"
                      >
                        <td className="p-3"><span className="text-xs font-medium text-black">Nulmeting</span></td>
                        <td className="p-3"><span className="text-xs text-neutral-500">-</span></td>
                        <td className="p-3">
                          <Badge className={nulmetingComplete ? "bg-green-100 text-green-700 text-[10px]" : "bg-amber-100 text-amber-700 text-[10px]"}>
                            {nulmetingComplete ? 'Afgerond' : 'Bezig'}
                          </Badge>
                        </td>
                      </tr>
                      {completedReassessments.map((r, idx) => (
                        <tr
                          key={r.id}
                          className={`border-b border-neutral-100 cursor-pointer transition-colors ${selectedHistory === r.id ? 'bg-neutral-100' : 'hover:bg-neutral-50'}`}
                          onClick={() => { setSelectedDomain(null); setSelectedHistory(r.id); }}
                          data-testid={`history-row-sp-${idx}`}
                        >
                          <td className="p-3"><span className="text-xs font-medium text-black">Steekproef {idx + 1}</span></td>
                          <td className="p-3"><span className="text-xs text-neutral-500">{r.completed_at ? new Date(r.completed_at).toLocaleDateString('nl-NL') : '-'}</span></td>
                          <td className="p-3"><Badge className="bg-green-100 text-green-700 text-[10px]">Afgerond</Badge></td>
                        </tr>
                      ))}
                      {activeReassessment && (
                        <tr
                          className={`border-b border-neutral-100 cursor-pointer transition-colors ${selectedHistory === 'active-sp' ? 'bg-amber-50' : 'hover:bg-amber-50/40'}`}
                          onClick={() => { setSelectedDomain(null); setSelectedHistory('active-sp'); }}
                          data-testid="history-row-active"
                        >
                          <td className="p-3"><span className="text-xs font-medium text-amber-700">Steekproef {completedReassessments.length + 1}</span></td>
                          <td className="p-3"><span className="text-xs text-amber-600">{activeReassessment.started_at ? new Date(activeReassessment.started_at).toLocaleDateString('nl-NL') : '-'}</span></td>
                          <td className="p-3"><Badge className="bg-amber-100 text-amber-700 text-[10px]">Bezig</Badge></td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </section>
            )}
          </main>
        </div>

        {/* ===== RIGHT PANEL: Domain detail ===== */}
        <RightPanel
          open={!!selectedDomain && !!selectedMod}
          onClose={() => setSelectedDomain(null)}
          title={selectedMod?.title || ''}
          subtitle={selectedMod ? (
            <Badge className={`text-[10px] ${
              getStatus(selectedMod.id) === 'completed' ? 'bg-green-100 text-green-700' :
              getStatus(selectedMod.id) === 'reassessing' ? 'bg-amber-100 text-amber-700' :
              getProgress(selectedMod.id) > 0 ? 'bg-blue-100 text-blue-700' :
              'bg-neutral-100 text-neutral-500'
            }`}>
              {getStatus(selectedMod.id) === 'completed' ? 'Voltooid' :
               getStatus(selectedMod.id) === 'reassessing' ? 'Steekproef' :
               getProgress(selectedMod.id) > 0 ? `${getProgress(selectedMod.id)}% klaar` :
               'Niet gestart'}
            </Badge>
          ) : null}
          testId="domain-right-panel"
          actions={selectedMod ? (
            <>
              <div className="flex-1" />
              <Button
                className="bg-black text-white hover:bg-neutral-800 text-xs"
                onClick={() => navigate(selectedMod.href)}
                data-testid="domain-panel-cta"
              >
                {getStatus(selectedMod.id) === 'completed' ? 'Bekijken' :
                 getProgress(selectedMod.id) > 0 ? 'Ga verder' : 'Start'}
                <ArrowRight className="h-3.5 w-3.5 ml-1.5" strokeWidth={1.5} />
              </Button>
            </>
          ) : null}
        >
          {selectedMod && (
            <div className="space-y-8">
              {/* Steekproef context: Nulmeting referentie */}
              {isModuleReassessing(selectedMod.id) && nulmetingSnapshot && (() => {
                const scoreKey = `${selectedMod.id}_score`;
                const nulScore = nulmetingSnapshot[scoreKey];
                return (
                  <div className="rounded-xl border border-amber-200 bg-amber-50/30 p-4 space-y-3" data-testid="panel-steekproef-context">
                    <div className="flex items-center gap-2">
                      <RotateCcw className="h-3.5 w-3.5 text-amber-600" strokeWidth={1.5} />
                      <p className="text-xs font-semibold text-amber-800">Steekproef actief</p>
                    </div>
                    <p className="text-[11px] text-amber-700">Voer de taken opnieuw uit. Eerder voltooide resultaten tellen niet mee.</p>
                    {nulScore !== null && nulScore !== undefined && (
                      <div className="flex items-center justify-between pt-2 border-t border-amber-200/60">
                        <span className="text-[11px] text-neutral-500 flex items-center gap-1.5">
                          <CheckCircle className="h-3 w-3 text-neutral-400" strokeWidth={1.5} />
                          Nulmeting score
                        </span>
                        <span className="text-sm font-bold text-neutral-600">{nulScore}%</span>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Score — huidige fase */}
              <div className="text-center p-6 rounded-xl bg-neutral-50 border border-neutral-100">
                <p className="text-[10px] font-bold text-neutral-400 uppercase tracking-wider mb-1">
                  {isModuleReassessing(selectedMod.id) ? 'Steekproef voortgang' : 'Score'}
                </p>
                <p className="text-3xl font-bold text-black">{getModuleActualScore(selectedMod.id)}%</p>
                <p className="text-xs text-neutral-500 mt-1">{getModuleScoreLabel(selectedMod.id)}</p>
              </div>

              {/* Taken — met label of het nulmeting of steekproef taken zijn */}
              <div>
                <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-400 mb-3">
                  {isModuleReassessing(selectedMod.id) ? 'Steekproef taken' : 'Taken'}
                </p>
                <div className="space-y-2">
                  {getModuleSteps(selectedMod.id).map((step, i) => (
                    <div key={i} className={`flex items-center gap-3 p-3 rounded-lg border ${step.done ? 'border-green-200 bg-green-50/40' : 'border-neutral-100'}`} data-testid={`domain-task-${i}`}>
                      <div className={`h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${step.done ? 'bg-green-600 text-white' : 'bg-neutral-100 text-neutral-400'}`}>
                        {step.done ? <CheckCircle className="h-3 w-3" strokeWidth={2.5} /> : i + 1}
                      </div>
                      <span className={`text-xs ${step.done ? 'text-green-700 font-medium' : 'text-neutral-600'}`}>{step.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Detail metrics */}
              <div>
                <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-400 mb-3">Details</p>
                <div className="space-y-2">
                  {getModuleDetailMetrics(selectedMod.id).map((m, i) => (
                    <div key={i} className="flex items-center justify-between py-2 border-b border-neutral-100 last:border-0">
                      <span className="text-xs text-neutral-500">{m.label}</span>
                      <span className="text-xs font-semibold text-black">{m.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Geschatte tijd */}
              <div className="flex items-center gap-2 text-[11px] text-neutral-400">
                <Clock className="h-3.5 w-3.5" strokeWidth={1.5} />
                <span>Geschatte tijd: {selectedMod.time}</span>
              </div>
            </div>
          )}
        </RightPanel>

        {/* ===== RIGHT PANEL: History detail ===== */}
        <RightPanel
          open={!!selectedHistory}
          onClose={() => { setSelectedHistory(null); setHrAnswers(null); }}
          title={selectedHistory === 'nulmeting' ? 'Nulmeting' : selectedHistory === 'active-sp' ? `Steekproef ${completedReassessments.length + 1}` : (() => {
            const idx = completedReassessments.findIndex(r => r.id === selectedHistory);
            return idx >= 0 ? `Steekproef ${idx + 1}` : 'Meting';
          })()}
          subtitle={
            selectedHistory === 'nulmeting' ? (
              <Badge className={nulmetingComplete ? 'bg-green-100 text-green-700 text-[10px]' : 'bg-amber-100 text-amber-700 text-[10px]'}>
                {nulmetingComplete ? 'Afgerond' : 'Bezig'}
              </Badge>
            ) : selectedHistory === 'active-sp' ? (
              <Badge className="bg-amber-100 text-amber-700 text-[10px]">Bezig</Badge>
            ) : (
              <Badge className="bg-green-100 text-green-700 text-[10px]">Afgerond</Badge>
            )
          }
          testId="history-right-panel"
          actions={selectedHistory === 'active-sp' ? (
            <>
              <div className="flex-1" />
              <Button className="bg-black text-white text-xs hover:bg-neutral-800" onClick={() => navigate('/reassessment')} data-testid="history-panel-go-reassessment">
                Ga verder <ArrowRight className="h-3.5 w-3.5 ml-1.5" strokeWidth={1.5} />
              </Button>
            </>
          ) : nulmetingComplete ? (
            <>
              <div className="flex-1" />
              <Button variant="outline" size="sm" className="text-xs" onClick={() => navigate('/reassessment')} data-testid="history-panel-plan-sp">
                <RotateCcw className="h-3 w-3 mr-1" strokeWidth={1.5} /> Plan steekproef
              </Button>
              <Button className="bg-black text-white text-xs hover:bg-neutral-800" onClick={() => navigate('/risks')} data-testid="history-panel-go-risks">
                Bekijk risico's <ArrowRight className="h-3.5 w-3.5 ml-1.5" strokeWidth={1.5} />
              </Button>
            </>
          ) : null}
        >
          <div className="space-y-8">
            {/* Total score */}
            <div className="text-center p-6 rounded-xl bg-neutral-50 border border-neutral-100" data-testid="history-total-score">
              <p className="text-[10px] font-bold text-neutral-400 uppercase tracking-wider mb-1">Totaalscore</p>
              <p className="text-3xl font-bold text-black">{totalScore}%</p>
            </div>

            {/* Per domain */}
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-400 mb-3">Per domein</p>
              <div className="space-y-3">
                {MODULES.map(mod => (
                  <div key={mod.id} className="flex items-center justify-between p-3 rounded-lg border border-neutral-100">
                    <div className="flex items-center gap-3">
                      <mod.icon className="h-4 w-4 text-neutral-400" strokeWidth={1.5} />
                      <span className="text-xs font-medium text-black">{mod.title}</span>
                    </div>
                    <div className="text-right">
                      <span className="text-sm font-bold text-black">{getModuleActualScore(mod.id)}%</span>
                      <p className="text-[10px] text-neutral-400">{getModuleScoreLabel(mod.id)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Steekproef score improvement */}
            {selectedHistory && selectedHistory !== 'nulmeting' && selectedHistory !== 'active-sp' && (() => {
              const r = completedReassessments.find(re => re.id === selectedHistory);
              if (!r?.results?.score_improvement) return null;
              return (
                <div className={`p-4 rounded-xl border ${r.results.score_improvement > 0 ? 'border-green-200 bg-green-50/40' : 'border-red-200 bg-red-50/40'}`}>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-400 mb-1">Verschil t.o.v. vorige meting</p>
                  <p className={`text-xl font-bold ${r.results.score_improvement > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {r.results.score_improvement > 0 ? '+' : ''}{r.results.score_improvement} punten
                  </p>
                </div>
              );
            })()}

            {/* Answers (only for nulmeting) */}
            {selectedHistory === 'nulmeting' && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-400">Vraaginzage</p>
                  {!hrAnswers && !answersLoading && (
                    <Button variant="outline" size="sm" className="text-xs h-7" onClick={fetchAnswers} data-testid="load-answers-btn">
                      <Eye className="h-3 w-3 mr-1" strokeWidth={1.5} /> Inzien
                    </Button>
                  )}
                </div>
                {answersLoading && (
                  <div className="flex items-center gap-2 py-3">
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-neutral-400" strokeWidth={1.5} />
                    <span className="text-xs text-neutral-400">Laden...</span>
                  </div>
                )}
                {hrAnswers?.answers?.length > 0 && (
                  <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1" data-testid="answers-list">
                    {hrAnswers.answers.map((a, i) => {
                      const answerLabels = { yes: 'Ja', partial: 'Gedeeltelijk', no: 'Nee', unknown: 'Onbekend' };
                      const answerColors = { yes: 'bg-green-100 text-green-700', partial: 'bg-amber-100 text-amber-700', no: 'bg-red-100 text-red-700', unknown: 'bg-neutral-100 text-neutral-500' };
                      return (
                        <div key={i} className="p-3 rounded-lg border border-neutral-100" data-testid={`answer-row-${i}`}>
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-2 flex-1 min-w-0">
                              <span className="text-[10px] font-bold text-neutral-400 bg-neutral-100 rounded px-1.5 py-0.5 shrink-0">V{i + 1}</span>
                              <p className="text-xs text-black leading-relaxed">{a.question}</p>
                            </div>
                            <Badge className={`text-[10px] shrink-0 ${answerColors[a.answer] || answerColors.unknown}`}>
                              {answerLabels[a.answer] || a.answer}
                            </Badge>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Recommended next steps */}
            {nulmetingComplete && selectedHistory === 'nulmeting' && (
              <div className="p-4 rounded-xl bg-neutral-50 border border-neutral-100">
                <p className="text-[10px] font-bold text-neutral-400 uppercase tracking-wider mb-2">Aanbevolen</p>
                <div className="space-y-2 text-xs text-neutral-700">
                  <div className="flex items-center gap-2 cursor-pointer hover:text-black" onClick={() => navigate('/risks')}>
                    <ArrowRight className="h-3 w-3 shrink-0" strokeWidth={1.5} /><span>Bekijk risico's</span>
                  </div>
                  <div className="flex items-center gap-2 cursor-pointer hover:text-black" onClick={() => navigate('/measures')}>
                    <ArrowRight className="h-3 w-3 shrink-0" strokeWidth={1.5} /><span>Plan maatregelen</span>
                  </div>
                  <div className="flex items-center gap-2 cursor-pointer hover:text-black" onClick={() => navigate('/reassessment')}>
                    <ArrowRight className="h-3 w-3 shrink-0" strokeWidth={1.5} /><span>Plan steekproef</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </RightPanel>
      </div>
    </MainLayout>
  );
};

export default AssessmentPage;
